import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../apiAdmin";
import { GrowthReport, SyncResult } from "./admin-growth/types";
import { fmtDay, fmtN, pctOf } from "./admin-growth/format";
import { Fx } from "./admin-growth/Sheet";
import { FunnelSheet } from "./admin-growth/FunnelSheet";
import { ExitsSheet } from "./admin-growth/ExitsSheet";
import { AppStoreCard } from "./admin-growth/AppStoreCard";
import { GrowthMoreDetail } from "./admin-growth/MoreDetail";
import { TalentEmailsSheet } from "./admin-growth/TalentEmailsSheet";

// Admin "Growth Funnel" tab — a workbook, not a dashboard (founder pick
// 9/21: "a simple Excel-looking spreadsheet, focus on where people drop
// off"). Everything is computed live server-side (GET /admin/growth,
// lib/growthReport.js); this component only renders. Apple's listing numbers
// arrive through the daily App Store Connect sync and the "Sync now" button.
//
// One sheet per tab, one "fx" sentence at the top of each:
//   Talent drop-off · Talent emails · Fans · App Store · Where people leave · Details.
//   Nothing from the old layout is gone — it moved (segments sit in Details).
//   Talent emails loads its own report with its own 7 / 30 / 90-day switch
//   (GET /admin/talent-emails); the range pills above don't apply to it.
//
// Founder-locked rules:
//   - Talent and Fan conversion are NEVER blended into one number.
//   - Drop-off is NORMAL, never styled as an error — the yellow cell says
//     "look here first"; red is reserved for true errors.
//   - Counts PEOPLE, never money — revenue lives in the Revenue tab.

const RANGES: { key: string; label: string }[] = [
  { key: "1", label: "1d" },
  { key: "7", label: "7d" },
  { key: "30", label: "30d" },
  { key: "365", label: "1y" },
  { key: "custom", label: "Custom" },
];

type SheetKey = "talent" | "emails" | "fans" | "appstore" | "exits" | "details";
const SHEETS: { key: SheetKey; label: string }[] = [
  { key: "talent", label: "Talent drop-off" },
  { key: "emails", label: "Talent emails" },
  { key: "fans", label: "Fans" },
  { key: "appstore", label: "App Store" },
  { key: "exits", label: "Where people leave" },
  { key: "details", label: "Details" },
];
const SHEET_KEY = "gr-sheet";

export default function AdminGrowthTab({ onShowNeedsBank }: { onShowNeedsBank?: () => void }) {
  const [report, setReport] = useState<GrowthReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<string>("7");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customApplied, setCustomApplied] = useState<{ from: string; to: string } | null>(null);
  const [sheet, setSheet] = useState<SheetKey>(() => {
    try {
      const saved = localStorage.getItem(SHEET_KEY);
      return SHEETS.some((s) => s.key === saved) ? (saved as SheetKey) : "talent";
    } catch {
      return "talent";
    }
  });
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params =
        range === "custom" && customApplied ? { from: customApplied.from, to: customApplied.to } : { days: range };
      const res = await adminApi.get<GrowthReport>("/growth", { params });
      setReport(res.data);
    } catch {
      setError("Couldn't load the growth funnel. Retry in a moment.");
    } finally {
      setLoading(false);
    }
  }, [range, customApplied]);

  useEffect(() => {
    if (range === "custom" && !customApplied) return; // wait for Apply
    void load();
  }, [load, range, customApplied]);

  const syncNow = useCallback(async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await adminApi.post<SyncResult>("/growth/appstore/sync");
      const d = res.data;
      if (d.skipped) setSyncMsg(d.reason || "Not configured yet.");
      else if (d.ok) setSyncMsg(`Synced — ${d.sales?.days ?? 0} download day(s), ${d.analytics?.days ?? 0} listing day(s).`);
      else setSyncMsg((d.errors || []).join(" · ") || "Sync reported a problem.");
      await load();
    } catch {
      setSyncMsg("Sync failed — the server didn't answer. Try again in a minute.");
    } finally {
      setSyncing(false);
    }
  }, [load]);

  const pickSheet = (k: SheetKey) => {
    setSheet(k);
    try {
      localStorage.setItem(SHEET_KEY, k);
    } catch {
      /* per-viewer convenience only */
    }
  };

  if (loading && !report) return <div className="adm-card gr-muted">Loading the growth funnel…</div>;
  if (error && !report)
    return (
      <div className="adm-card">
        <span className="gr-muted">{error}</span>{" "}
        <button className="gr-retry" onClick={() => void load()}>
          Retry
        </button>
      </div>
    );
  if (!report) return null;

  const { range: win, appStore } = report;
  const windowStartsBeforeData = win.dataStart != null && new Date(win.from) < new Date(win.dataStart);

  return (
    <div className="gr-wrap">
      {/* header: range pills */}
      <div className="gr-top">
        <span className="gr-cmp">
          {fmtDay(win.from)} – {fmtDay(new Date(new Date(win.to).getTime() - 1).toISOString())} · compared with{" "}
          {fmtDay(win.prevFrom)} – {fmtDay(new Date(new Date(win.prevTo).getTime() - 1).toISOString())}
          {loading ? " · refreshing…" : ""}
        </span>
        <div className="gr-pills">
          {RANGES.map((r) => (
            <button
              key={r.key}
              className={range === r.key ? "on" : ""}
              onClick={() => {
                setRange(r.key);
                if (r.key !== "custom") setCustomApplied(null);
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {range === "custom" && (
        <div className="gr-custom">
          <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          <span>to</span>
          <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          <button
            className="gr-apply"
            disabled={!customFrom || !customTo}
            onClick={() => setCustomApplied({ from: customFrom, to: customTo })}
          >
            Apply
          </button>
        </div>
      )}
      {windowStartsBeforeData && win.dataStart && (
        <div className="gr-datastart">
          Live tracking started {fmtDay(win.dataStart)} — earlier parts of this window rely on backfilled history (signups, invite
          codes, activations) and have no visit or session data.
        </div>
      )}

      <div className="gr-book">
        <div className="gr-book-tabs" role="tablist">
          {SHEETS.map((s) => (
            <button key={s.key} role="tab" aria-selected={sheet === s.key} className={sheet === s.key ? "on" : ""} onClick={() => pickSheet(s.key)}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="gr-book-body">
          {sheet === "talent" && <FunnelSheet path="dj" funnels={report.funnels} onShowNeedsBank={onShowNeedsBank} />}
          {sheet === "emails" && <TalentEmailsSheet />}
          {sheet === "fans" && <FunnelSheet path="fan" funnels={report.funnels} />}
          {sheet === "appstore" && (
            <>
              <Fx>
                {!appStore.configured
                  ? "App Store Connect isn't connected yet — the steps are below."
                  : appStore.cur.downloads == null
                  ? "Waiting for Apple's first report."
                  : `${fmtN(appStore.cur.downloads)} downloads from ${fmtN(appStore.cur.pageViews)} listing views (${pctOf(appStore.cur.downloads, appStore.cur.pageViews)}); ${fmtN(report.kpis.visitors.app)} app opens.`}
              </Fx>
              <AppStoreCard info={appStore} appOpens={report.kpis.visitors.app || 0} onSync={() => void syncNow()} syncing={syncing} syncMsg={syncMsg} />
            </>
          )}
          {sheet === "exits" && <ExitsSheet exits={report.exits} />}
          {sheet === "details" && <GrowthMoreDetail report={report} />}
        </div>
      </div>

      <div className="gr-elsewhere">
        <span>
          Lives elsewhere on purpose → <b>Revenue amounts:</b> Revenue tab
        </span>
        <span>
          <b>Payout details:</b> All Payouts
        </span>
        <span>
          <b>Market open/close:</b> Countries tab
        </span>
      </div>
    </div>
  );
}
